import { useState, useEffect, useRef, useCallback } from "react";
import { useChatPresence } from "../../../contexts/ChatPresenceContext";
import { getUserUuid } from "../utils/auth";

const useChatSocket = () => {
  const { socketRef } = useChatPresence();
  const [threadPresence, setThreadPresence] = useState(new Map());
  const [threadTypingUsers, setThreadTypingUsers] = useState(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const currentThreadRef = useRef(null);

  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    const myUuid = getUserUuid();

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    const onPresence = (data) => {
      setThreadPresence((prev) => {
        const next = new Map(prev);
        const current = new Set(next.get(data.thread_id) || []);
        if (data.status === "joined") {
          if (data.uuid !== myUuid) current.add(data.uuid);
        } else {
          current.delete(data.uuid);
        }
        if (current.size === 0) next.delete(data.thread_id);
        else next.set(data.thread_id, current);
        return next;
      });
    };

    const onPresenceSync = (data) => {
      setThreadPresence((prev) => {
        const next = new Map(prev);
        const others = (data.present_uuids || []).filter((u) => u !== myUuid);
        if (others.length === 0) next.delete(data.thread_id);
        else next.set(data.thread_id, new Set(others));
        return next;
      });
    };

    const onTyping = (data) => {
      if (data.uuid === myUuid) return;
      setThreadTypingUsers((prev) => {
        const next = new Map(prev);
        const threadMap = new Map(next.get(data.thread_id) || []);
        threadMap.set(data.uuid, data.name);
        next.set(data.thread_id, threadMap);
        return next;
      });
    };

    const onStopTyping = (data) => {
      setThreadTypingUsers((prev) => {
        const next = new Map(prev);
        const threadMap = new Map(next.get(data.thread_id) || []);
        threadMap.delete(data.uuid);
        if (threadMap.size === 0) next.delete(data.thread_id);
        else next.set(data.thread_id, threadMap);
        return next;
      });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("thread_presence", onPresence);
    socket.on("thread_presence_sync", onPresenceSync);
    socket.on("thread_typing", onTyping);
    socket.on("thread_stop_typing", onStopTyping);

    // If already connected, set state
    if (socket.connected) setIsConnected(true);

    // Rejoin current thread on reconnect
    const onReconnect = () => {
      if (currentThreadRef.current) {
        socket.emit("join_thread", { thread_id: currentThreadRef.current });
      }
    };
    socket.on("connect", onReconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("thread_presence", onPresence);
      socket.off("thread_presence_sync", onPresenceSync);
      socket.off("thread_typing", onTyping);
      socket.off("thread_stop_typing", onStopTyping);
      socket.off("connect", onReconnect);
    };
  }, [socketRef]);

  const joinThread = useCallback(
    (threadId) => {
      const socket = socketRef?.current;
      if (!socket || !threadId) return;
      if (currentThreadRef.current && currentThreadRef.current !== threadId) {
        socket.emit("leave_thread", { thread_id: currentThreadRef.current });
      }
      socket.emit("join_thread", { thread_id: threadId });
      currentThreadRef.current = threadId;
    },
    [socketRef],
  );

  const leaveThread = useCallback(
    (threadId) => {
      const socket = socketRef?.current;
      if (!socket || !threadId) return;
      socket.emit("leave_thread", { thread_id: threadId });
      if (currentThreadRef.current === threadId)
        currentThreadRef.current = null;
    },
    [socketRef],
  );

  const cancelStream = useCallback(
    (threadId, documentId) => {
      socketRef?.current?.emit("cancel_stream", {
        thread_id: threadId,
        document_id: documentId,
      });
    },
    [socketRef],
  );

  const emitTyping = useCallback(
    (threadId) => {
      socketRef?.current?.emit("thread_typing", { thread_id: threadId });
    },
    [socketRef],
  );

  const emitStopTyping = useCallback(
    (threadId) => {
      socketRef?.current?.emit("thread_stop_typing", { thread_id: threadId });
    },
    [socketRef],
  );

  return {
    socketRef,
    threadPresence,
    threadTypingUsers,
    joinThread,
    leaveThread,
    cancelStream,
    emitTyping,
    emitStopTyping,
    isConnected,
  };
};

export default useChatSocket;
